import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, 
  ChevronRight, 
  ChevronLeft, 
  Check,
  DollarSign,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Home,
  Briefcase,
  Shield,
  Lock
} from "lucide-react";
import { Slider } from "@/components/ui/slider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CANADIAN_PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
  "Northwest Territories", "Nova Scotia", "Nunavut", "Ontario", "Prince Edward Island",
  "Quebec", "Saskatchewan", "Yukon"
];

const VEHICLE_TYPES = [
  { id: "sedan", label: "Sedan", icon: Car },
  { id: "suv", label: "SUV", icon: Car },
  { id: "truck", label: "Truck", icon: Car },
  { id: "coupe", label: "Coupe", icon: Car },
  { id: "van", label: "Van", icon: Car },
  { id: "other", label: "Other", icon: Car },
];

const INCOME_SOURCES = [
  "Employed (Full-Time)",
  "Employed (Part-Time)",
  "Self-Employed",
  "Retired",
  "Disability",
  "Social Security",
  "Other"
];

export const ApplyPage = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  const [formData, setFormData] = useState({
    car_condition: "",
    budget: 25000,
    vehicle_type: "",
    make_model: "",
    full_name: "",
    phone: "",
    email: "",
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
    date_of_birth: "",
    home_ownership: "",
    monthly_payment: "",
    residency_length: "",
    income_source: "",
    income_length: "",
    website: ""
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatBudget = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        return formData.car_condition && formData.budget > 0;
      case 2:
        return formData.vehicle_type;
      case 3:
        return formData.full_name && formData.phone && formData.email && 
               formData.street_address && formData.city && formData.state && formData.zip_code;
      case 4:
        return formData.date_of_birth && formData.home_ownership && 
               formData.monthly_payment && formData.residency_length &&
               formData.income_source && formData.income_length;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        budget: parseInt(formData.budget),
        monthly_payment: parseInt(formData.monthly_payment) || 0
      };

      await axios.post(`${API}/leads`, submitData);
      setIsComplete(true);
      toast.success("Your application has been submitted!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <div className="apply-progress">
      <div className="progress-steps-indicator">
        {[1, 2, 3, 4].map((s) => (
          <div 
            key={s} 
            className={`progress-dot ${s <= step ? 'active' : ''} ${s < step ? 'completed' : ''}`}
          >
            {s < step ? <Check size={14} /> : s}
          </div>
        ))}
      </div>
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${(step / 4) * 100}%` }}
        />
      </div>
      <p className="progress-text">Step {step} of 4</p>
    </div>
  );

  const renderStep1 = () => (
    <div className="form-step slide-in" data-testid="step-1-budget">
      <h2>What type of vehicle are you looking for?</h2>
      <p className="step-subtitle">Let us know your preferences so we can find the best options for you.</p>
      
      <div className="form-group">
        <label className="form-label">New or Used Vehicle?</label>
        <div className="condition-buttons">
          <button
            type="button"
            className={`condition-btn ${formData.car_condition === 'New' ? 'selected' : ''}`}
            onClick={() => updateField('car_condition', 'New')}
            data-testid="condition-new"
          >
            New Vehicle
          </button>
          <button
            type="button"
            className={`condition-btn ${formData.car_condition === 'Used' ? 'selected' : ''}`}
            onClick={() => updateField('car_condition', 'Used')}
            data-testid="condition-used"
          >
            Used Vehicle
          </button>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">
          <DollarSign size={16} /> Your Budget
        </label>
        <div className="budget-display" data-testid="budget-display">
          {formatBudget(formData.budget)}
        </div>
        <Slider
          value={[formData.budget]}
          onValueChange={(value) => updateField('budget', value[0])}
          min={5000}
          max={100000}
          step={1000}
          data-testid="budget-slider"
        />
        <div className="budget-range">
          <span>$5,000</span>
          <span>$100,000</span>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="form-step slide-in" data-testid="step-2-vehicle">
      <h2>What type of vehicle do you prefer?</h2>
      <p className="step-subtitle">Select your preferred vehicle type</p>

      <div className="vehicle-grid">
        {VEHICLE_TYPES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`vehicle-card ${formData.vehicle_type === id ? 'selected' : ''}`}
            onClick={() => updateField('vehicle_type', id)}
            data-testid={`vehicle-${id}`}
          >
            <Icon size={28} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className="form-group" style={{ marginTop: '1.5rem' }}>
        <label className="form-label">Preferred Make/Model (Optional)</label>
        <input
          type="text"
          className="form-input"
          placeholder="e.g., Toyota Camry, Honda CR-V"
          value={formData.make_model}
          onChange={(e) => updateField('make_model', e.target.value)}
          data-testid="make-model-input"
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-step slide-in" data-testid="step-3-contact">
      <h2>Your Contact Information</h2>
      <p className="step-subtitle">We'll use this to contact you about your approval</p>

      <div className="form-group">
        <label className="form-label"><User size={14} /> Full Name *</label>
        <input
          type="text"
          className="form-input"
          placeholder="John Smith"
          value={formData.full_name}
          onChange={(e) => updateField('full_name', e.target.value)}
          data-testid="full-name-input"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label"><Phone size={14} /> Phone *</label>
          <input
            type="tel"
            className="form-input"
            placeholder="(416) 555-1234"
            value={formData.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            data-testid="phone-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label"><Mail size={14} /> Email *</label>
          <input
            type="email"
            className="form-input"
            placeholder="john@email.com"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            data-testid="email-input"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label"><MapPin size={14} /> Street Address *</label>
        <input
          type="text"
          className="form-input"
          placeholder="123 Main Street"
          value={formData.street_address}
          onChange={(e) => updateField('street_address', e.target.value)}
          data-testid="street-input"
        />
      </div>

      <div className="form-row-3">
        <div className="form-group">
          <label className="form-label">City *</label>
          <input
            type="text"
            className="form-input"
            placeholder="Toronto"
            value={formData.city}
            onChange={(e) => updateField('city', e.target.value)}
            data-testid="city-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Province *</label>
          <select
            className="form-input form-select"
            value={formData.state}
            onChange={(e) => updateField('state', e.target.value)}
            data-testid="state-select"
          >
            <option value="">Select</option>
            {CANADIAN_PROVINCES.map(province => (
              <option key={province} value={province}>{province}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Postal Code *</label>
          <input
            type="text"
            className="form-input"
            placeholder="M5V 1A1"
            value={formData.zip_code}
            onChange={(e) => updateField('zip_code', e.target.value)}
            data-testid="zip-input"
          />
        </div>
      </div>

      <div className="hp-field" aria-hidden="true">
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={formData.website}
          onChange={(e) => updateField('website', e.target.value)}
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="form-step slide-in" data-testid="step-4-personal">
      <h2>A Few More Details</h2>
      <p className="step-subtitle">This helps us find the best financing options for you</p>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label"><Calendar size={14} /> Date of Birth *</label>
          <input
            type="date"
            className="form-input"
            value={formData.date_of_birth}
            onChange={(e) => updateField('date_of_birth', e.target.value)}
            data-testid="dob-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label"><Home size={14} /> Housing Status *</label>
          <select
            className="form-input form-select"
            value={formData.home_ownership}
            onChange={(e) => updateField('home_ownership', e.target.value)}
            data-testid="ownership-select"
          >
            <option value="">Select</option>
            <option value="Rent">Rent</option>
            <option value="Own">Own</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Monthly Housing Payment *</label>
          <input
            type="number"
            className="form-input"
            placeholder="1500"
            value={formData.monthly_payment}
            onChange={(e) => updateField('monthly_payment', e.target.value)}
            data-testid="monthly-payment-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Time at Current Address *</label>
          <select
            className="form-input form-select"
            value={formData.residency_length}
            onChange={(e) => updateField('residency_length', e.target.value)}
            data-testid="residency-select"
          >
            <option value="">Select</option>
            <option value="Less than 1 year">Less than 1 year</option>
            <option value="1-2 years">1-2 years</option>
            <option value="2-5 years">2-5 years</option>
            <option value="5+ years">5+ years</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label"><Briefcase size={14} /> Employment Status *</label>
          <select
            className="form-input form-select"
            value={formData.income_source}
            onChange={(e) => updateField('income_source', e.target.value)}
            data-testid="income-source-select"
          >
            <option value="">Select</option>
            {INCOME_SOURCES.map(source => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Time at Current Job *</label>
          <select
            className="form-input form-select"
            value={formData.income_length}
            onChange={(e) => updateField('income_length', e.target.value)}
            data-testid="income-length-select"
          >
            <option value="">Select</option>
            <option value="Less than 6 months">Less than 6 months</option>
            <option value="6 months - 1 year">6 months - 1 year</option>
            <option value="1-2 years">1-2 years</option>
            <option value="2-5 years">2-5 years</option>
            <option value="5+ years">5+ years</option>
          </select>
        </div>
      </div>

      <div className="security-note">
        <Lock size={16} />
        <span>Your information is encrypted and secure. We never share your data.</span>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="form-step success-step fade-in" data-testid="step-success">
      <div className="success-icon">
        <Check strokeWidth={3} size={48} />
      </div>
      <h2>Application Submitted!</h2>
      <p>
        Thank you for applying with MotoMoneyFinance. One of our financing specialists 
        will review your application and contact you within 24 hours.
      </p>
      <div className="success-next">
        <h3>What happens next?</h3>
        <ul>
          <li><Check size={16} /> A specialist will review your application</li>
          <li><Check size={16} /> We'll match you with the best lenders</li>
          <li><Check size={16} /> You'll receive financing options by phone or email</li>
          <li><Check size={16} /> Choose your vehicle and drive away!</li>
        </ul>
      </div>
      <Link to="/" className="btn btn-primary" style={{ marginTop: '1.5rem' }}>
        Return to Home
      </Link>
    </div>
  );

  const renderStep = () => {
    if (isComplete) return renderSuccess();
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return renderStep1();
    }
  };

  return (
    <div className="apply-page">
      {/* Header */}
      <header className="apply-header">
        <Link to="/" className="logo" data-testid="apply-logo">
          <Car size={28} />
          <span>MotoMoney<strong>Finance</strong></span>
        </Link>
      </header>

      <div className="apply-container">
        <div className="apply-card">
          {!isComplete && (
            <div className="apply-card-header">
              <h1>Get Pre-Approved Today</h1>
              <p>Complete your application in just a few minutes</p>
              {renderProgressBar()}
            </div>
          )}

          <div className="apply-card-body">
            {renderStep()}

            {!isComplete && (
              <div className="form-nav">
                {step > 1 && (
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={handleBack}
                    data-testid="back-button"
                  >
                    <ChevronLeft size={18} />
                    Back
                  </button>
                )}
                
                {step < 4 ? (
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={handleNext}
                    disabled={!validateStep(step)}
                    data-testid="next-button"
                  >
                    Continue
                    <ChevronRight size={18} />
                  </button>
                ) : (
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !validateStep(step)}
                    data-testid="submit-button"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Application"}
                    {!isSubmitting && <Check size={18} />}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="apply-sidebar">
          <div className="sidebar-card">
            <Shield size={24} />
            <h3>100% Secure</h3>
            <p>Your data is protected with bank-level encryption</p>
          </div>
          <div className="sidebar-card">
            <Check size={24} />
            <h3>No Credit Impact</h3>
            <p>Checking your options won't affect your credit score</p>
          </div>
          <div className="sidebar-card">
            <Car size={24} />
            <h3>All Credit Welcome</h3>
            <p>Bad credit, no credit, bankruptcy — we can help</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="apply-footer">
        <p>&copy; {new Date().getFullYear()} MotoMoneyFinance. All rights reserved.</p>
        <Link to="/privacy">Privacy Policy</Link>
      </footer>
    </div>
  );
};

export default ApplyPage;
